import { useCallback } from 'react';
import { useDeviceSize } from './use-mobile';

type HapticPattern = 'success' | 'error' | 'warning' | 'selection';
type HapticMethod = () => Promise<boolean>;

interface HapticConfig {
  success: number[];
  error: number[];
  warning: number[];
  selection: number[];
}

const VIBRATION_PATTERNS: HapticConfig = {
  success: [50],
  error: [100, 50, 100],
  warning: [70, 40],
  selection: [40]
} as const;

/**
 * Enhanced haptic feedback hook with better error handling and device detection
 */
export function useHapticFeedback() {
  const deviceSize = useDeviceSize();
  const isSmallDevice = ['iPhoneSE', 'iPhonePro', 'mobile'].includes(deviceSize);

  const triggerHaptic = useCallback(async (pattern: HapticPattern = 'selection') => {
    if (!isSmallDevice) {
      // Silently skip haptics on non-mobile devices
      return;
    }

    try {
      // Enhanced iOS detection
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      const isWebKit = 'webkitHapticFeedback' in navigator;

      if (isIOS) {
        // Try iOS-specific haptics methods
        const methods: HapticMethod[] = [
          // Method 1: Modern iOS Haptics API
          async () => {
            if ('Haptics' in window) {
              const haptics = (window as any).Haptics;
              switch (pattern) {
                case 'success':
                  await haptics.impact({ style: 'light' });
                  break;
                case 'error':
                  await haptics.notification({ type: 'error' });
                  break;
                case 'warning':
                  await haptics.notification({ type: 'warning' });
                  break;
                case 'selection':
                  await haptics.selection();
                  break;
              }
              return true;
            }
            return false;
          },

          // Method 2: WebKit Haptic Engine
          async () => {
            if (isWebKit) {
              await (navigator as any).webkitHapticFeedback.engage();
              return true;
            }
            return false;
          },

          // Method 3: TouchEngine API
          async () => {
            if ('TouchEngine' in window) {
              await (window as any).TouchEngine.impact({ style: 'light' });
              return true;
            }
            return false;
          }
        ];

        // Try each method until one works
        for (const method of methods) {
          try {
            if (await method()) return;
          } catch (error) {
            // Silently continue to next method
          }
        }
      }

      // Fallback to standard vibration API
      if (!window.navigator.vibrate) {
        return;
      }

      // Use vibration API without logging
      await window.navigator.vibrate(VIBRATION_PATTERNS[pattern]);

    } catch (error) {
      // Silently fail - haptic feedback is non-critical
    }
  }, [isSmallDevice]);

  return { triggerHaptic };
}